import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useSeo, SEO_KEYWORDS } from '../hooks/useSeo';

const PricingPage = () => {
  useSeo({ title: 'Pricing | InvestiGate Case Management Software Plans from $49/mo', description: 'Compare InvestiGate plans for private investigators and investigation teams. Case management, evidence storage, AI document analysis and reporting from $49 per month.', keywords: SEO_KEYWORDS });
  const { user } = useAuth();
  const [loading, setLoading] = useState(null);

  const handleSubscribe = async (packageId) => {
    if (!user || user === false) {
      window.location.href = '/register';
      return;
    }
    setLoading(packageId);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout`,
        { package_id: packageId, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = data.url;
    } catch (error) {
      toast.error('Failed to initiate checkout');
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <Shield className="w-8 h-8 text-blue-600" />
            <span className="text-2xl font-bold text-gray-900">InvestiGate</span>
          </Link>
          <div className="flex items-center space-x-4">
            {user && user !== false ? (
              <Link to="/dashboard" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition">Dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="text-gray-600 hover:text-gray-900">Login</Link>
                <Link to="/register" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition">Get Started</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold text-center text-gray-900 mb-4">Simple, Transparent Pricing</h1>
          <p className="text-xl text-center text-gray-600 mb-12">Choose the plan that fits your investigation needs</p>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <PricingCard
              title="Starter"
              price="49"
              description="Perfect for individual investigators"
              features={['Up to 10 cases', 'Basic evidence storage', 'AI document analysis', 'Email support']}
              packageId="starter"
              onSubscribe={handleSubscribe}
              loading={loading === 'starter'}
            />
            <PricingCard
              title="Professional"
              price="99"
              description="For growing investigation teams"
              features={['Unlimited cases', 'Advanced evidence management', 'Priority AI analysis', 'Team collaboration', 'Priority support']}
              packageId="professional"
              onSubscribe={handleSubscribe}
              loading={loading === 'professional'}
              highlighted
            />
            <PricingCard
              title="Enterprise"
              price="199"
              description="For large organizations"
              features={['Everything in Professional', 'Custom integrations', 'Dedicated support', 'Advanced security', 'Custom reporting', 'SLA guarantees']}
              packageId="enterprise"
              onSubscribe={handleSubscribe}
              loading={loading === 'enterprise'}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

const PricingCard = ({ title, price, description, features, packageId, onSubscribe, loading, highlighted }) => (
  <div className={`bg-white rounded-xl shadow-lg p-8 ${highlighted ? 'ring-2 ring-blue-600 scale-105' : ''}`}>
    <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 mb-4">{description}</p>
    <div className="mb-6">
      <span className="text-5xl font-bold text-gray-900">${price}</span>
      <span className="text-gray-600">/month</span>
    </div>
    <ul className="space-y-3 mb-8">
      {features.map((feature, i) => (
        <li key={i} className="flex items-start">
          <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
          <span className="text-gray-700">{feature}</span>
        </li>
      ))}
    </ul>
    <button
      onClick={() => onSubscribe(packageId)}
      disabled={loading}
      className={`w-full py-3 px-4 rounded-lg font-semibold transition ${highlighted ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'} disabled:opacity-50`}
    >
      {loading ? 'Loading...' : 'Subscribe'}
    </button>
  </div>
);

export default PricingPage;
